// src/hooks/useUpdateHeadTags.js
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';

const DYNAMIC_HEAD_ATTR = 'data-dynamic-head';

export const useUpdateHeadTags = (pageSlug = 'home') => {
  const { data: headers, isLoading: headersLoading, isError, error } = useQuery({
    queryKey: ['dynamicHeaders', pageSlug],
    queryFn: async () => {
      const response = await api.get('/web-content/get-web-content');
      return response.data.dataset;
    },
    select: (data) => {
      const webSettings = data?.data?.web_settings || {};
      const pageMetadata = data?.data?.page_metadata?.[pageSlug] || {};

      return {
        title: pageMetadata.title || webSettings.title || 'VidyaKasam',
        favicon: pageMetadata.favicon || webSettings.favicon || `${process.env.PUBLIC_URL || ''}/favicon.ico`,
        meta_description: pageMetadata.meta_description || '',
        meta_keywords: pageMetadata.meta_keywords || '',
        meta_author: pageMetadata.meta_author || '',
        meta_robots: pageMetadata.meta_robots || 'index, follow',
        canonical_url: pageMetadata.canonical_url || '',
        og_type: pageMetadata.og_type || 'website',
        og_url: pageMetadata.og_url || '',
        og_title: pageMetadata.og_title || '',
        og_description: pageMetadata.og_description || '',
        og_image: pageMetadata.og_image || '',
        og_site_name: pageMetadata.og_site_name || '',
        twitter_card: pageMetadata.twitter_card || 'summary_large_image',
        twitter_url: pageMetadata.twitter_url || '',
        twitter_title: pageMetadata.twitter_title || '',
        twitter_description: pageMetadata.twitter_description || '',
        twitter_image: pageMetadata.twitter_image || '',
        twitter_site: pageMetadata.twitter_site || '',
      };
    },
    refetchOnWindowFocus: false,
    retry: 3,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!headers || headersLoading) return;

    // Update title
    document.title = headers.title;

    // Update favicon
    let favicon = document.querySelector('link[rel="icon"]');
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = headers.favicon;

    // Helper function to update or create meta tags
    const updateMetaTag = (name, content, property = false) => {
      let meta = document.querySelector(
        property ? `meta[property="${name}"]` : `meta[name="${name}"]`
      );
      if (!meta && content) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.name = name;
        }
        meta.setAttribute(DYNAMIC_HEAD_ATTR, 'true');
        document.head.appendChild(meta);
      }
      if (meta && content) {
        meta.setAttribute(DYNAMIC_HEAD_ATTR, 'true');
        meta.content = content;
      } else if (meta && !content) {
        meta.remove();
      }
    };

    // Helper function to update or create link tags
    const updateLinkTag = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link && href) {
        link = document.createElement('link');
        link.rel = rel;
        link.setAttribute(DYNAMIC_HEAD_ATTR, 'true');
        document.head.appendChild(link);
      }
      if (link && href) {
        link.setAttribute(DYNAMIC_HEAD_ATTR, 'true');
        link.href = href;
      } else if (link && !href) {
        link.remove();
      }
    };

    // Update meta tags
    updateMetaTag('description', headers.meta_description);
    updateMetaTag('keywords', headers.meta_keywords);
    updateMetaTag('author', headers.meta_author);
    updateMetaTag('robots', headers.meta_robots);
    updateLinkTag('canonical', headers.canonical_url);
    updateMetaTag('og:type', headers.og_type, true);
    updateMetaTag('og:url', headers.og_url, true);
    updateMetaTag('og:title', headers.og_title, true);
    updateMetaTag('og:description', headers.og_description, true);
    updateMetaTag('og:image', headers.og_image, true);
    updateMetaTag('og:site_name', headers.og_site_name, true);
    updateMetaTag('twitter:card', headers.twitter_card);
    updateMetaTag('twitter:url', headers.twitter_url);
    updateMetaTag('twitter:title', headers.twitter_title);
    updateMetaTag('twitter:description', headers.twitter_description);
    updateMetaTag('twitter:image', headers.twitter_image);
    updateMetaTag('twitter:site', headers.twitter_site);

    document
      .querySelectorAll(`link[rel="canonical"][${DYNAMIC_HEAD_ATTR}="true"]`)
      .forEach((link) => {
        if (!headers.canonical_url) {
          link.remove();
        }
      });

  }, [headers, headersLoading]);

  return { headers, headersLoading, isError, error };
};
